import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, must, type ClaimView } from "../api/client";
import { StampCard } from "../components/StampCard";
import { PolygonLine } from "../components/PolygonLine";
import { Qr } from "../components/Qr";
import { useWallet } from "../wallet/context";
import { MAX_NOTE, vouchLink } from "../vouch/note";
import { attachArtifactToLink } from "../artifact/transit";

/** /c/{claim_id}: the stamp. Refreshes while open so a vouch signed on a phone appears live. */
export function ClaimPage() {
  const { id = "" } = useParams();
  const { wallet, identity } = useWallet();
  const [view, setView] = useState<ClaimView | null>(null);
  const [err, setErr] = useState("");
  const [link, setLink] = useState("");
  const [basis, setBasis] = useState("witnessed");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkNote, setLinkNote] = useState("");
  const [bases, setBases] = useState<{ id: string; label: string }[]>([]);

  const load = useCallback(() => api.GET("/claims/{claim_id}", { params: { path: { claim_id: id } } })
    .then((r) => (r.data ? setView(r.data) : setErr("Claim not found."))), [id]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.GET("/registry/bases").then((r) => setBases(r.data?.bases ?? [])); }, []);

  const ask = async () => {
    setErr(""); setLinkNote(""); setBusy(true);
    try {
      const jws = await wallet.signEnvelope("vouch_request", { claim_id: id, holder_did: identity!.did, proposed_basis: basis });
      const out = await must(api.POST("/claims/{claim_id}/requests", { params: { path: { claim_id: id } }, body: { jws } }));
      // The note goes only into the link's fragment, never into the request above.
      let l = vouchLink(window.location.origin, out.token, note);
      if (view?.claim.artifact_hash) {
        // This link's own encrypted copy, with its own key (in the fragment only).
        const prepared = await wallet.loadArtifact(id);
        if (prepared) l = await attachArtifactToLink(wallet, id, out.token, l, prepared);
        else setLinkNote("The picture is only on the device it was attached on, so this link has no picture.");
      }
      setLink(l);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  if (!view) return <p className={err ? "error" : "note"}>{err || "Loading…"}</p>;
  const isHolder = identity?.did === view.holder_did;
  return (
    <div>
      <StampCard view={view}>
        <PolygonLine entryHash={view.entry_hash} poll={5000} />
      </StampCard>
      {err && <p className="error" role="alert">{err}</p>}
      <div className="row" style={{ marginTop: 16 }}>
        <Link className="btn" to={`/c/${id}/verify`} data-testid="verify-link">Verify every signature</Link>
        <Link className="btn" to={`/d/${encodeURIComponent(view.holder_did)}`}>Holder's chain</Link>
        <button onClick={() => navigator.clipboard?.writeText(window.location.href)}>Share</button>
      </div>
      {isHolder && (
        <div className="card" style={{ marginTop: 16 }}>
          <b>Ask someone to vouch</b>
          <label htmlFor="pb">Proposed basis</label>
          <select id="pb" value={basis} onChange={(e) => setBasis(e.target.value)} data-testid="proposed-basis">
            {bases.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
          <label htmlFor="vn">Private note for the person you send it to (optional)</label>
          <input id="vn" value={note} maxLength={MAX_NOTE} onChange={(e) => setNote(e.target.value)} placeholder="e.g. From Asha, for my KBA capstone" data-testid="vouch-note" />
          <div className="hint">Only in the link itself, after the #: it never reaches the server or the public record. {MAX_NOTE - note.length} characters left.</div>
          <div className="row" style={{ marginTop: 12 }}><button className="primary" disabled={busy} onClick={ask} data-testid="ask-vouch">Create a one-time vouch link</button></div>
          {link && (<div style={{ marginTop: 12 }}>
            <Qr text={link} />
            <p className="mono" data-testid="vouch-link">{link}</p>
            {link.includes("k=") && <p className="hint">This link carries the key to its own encrypted copy of the picture. Send it only to the person you are asking.</p>}
          </div>)}
          {linkNote && <p className="note" data-testid="link-note">{linkNote}</p>}
        </div>
      )}
    </div>
  );
}
